import { useMutation } from '@tanstack/react-query'
import type {
  Address,
  AddressIssue,
  AddressSuggestion,
  ValidateAddressRequest,
  ValidateAddressResponse
} from '@itsrever/returns-api-types'
import { validateAddress } from '@/services/api/address-validation-api'
import { getStaticProvinces } from '@/utils/get-provinces'

export const useValidateAddress = () =>
  useMutation({
    // A best-effort check: offline it must fail and let the shopper continue, not pause.
    networkMode: 'always',
    mutationFn: ({
      ecommerceID,
      request
    }: {
      ecommerceID: string
      request: ValidateAddressRequest
    }) => validateAddress(ecommerceID, request)
  })

// Only a suggestion changes what the shopper sees. Anything else, including a
// failed call, continues exactly as before the check existed.
export const pickSuggestions = (
  response: ValidateAddressResponse | undefined
): AddressSuggestion[] =>
  response?.status === 'suggestion' ? (response.suggestions ?? []) : []

// Issues come with any status: the format rules answer even when no carrier
// source could check the locality.
export const pickIssues = (
  response: ValidateAddressResponse | undefined
): AddressIssue[] =>
  // The address step has no email field: the shopper could neither fix nor check it here.
  (response?.issues ?? []).filter((issue) => issue.field !== 'email')

// An issue with a suggested value is a fix the shopper can accept with one tap;
// one without is a hint they have to act on themselves.
export const isFixable = (issue: AddressIssue): boolean =>
  issue.suggested_value !== undefined && issue.suggested_value !== null

// Fields an issue can name, as the response spells them.
export type IssueField =
  | 'address_line_1'
  | 'address_line_2'
  | 'postcode'
  | 'state_province_code'
  | 'country_code'
  | 'phone'
  | 'email'

const ISSUE_FIELDS: ReadonlySet<string> = new Set<IssueField>([
  'address_line_1',
  'address_line_2',
  'postcode',
  'state_province_code',
  'country_code',
  'phone',
  'email'
])

export const isIssueField = (field: string): field is IssueField =>
  ISSUE_FIELDS.has(field)

// The generated type leaves every field optional; an issue without one names nothing.
export const fieldOf = (issue: AddressIssue): string => issue.field ?? ''

// The value the shopper typed for the field an issue names.
export const currentValue = (address: Address, field: string): string => {
  if (!isIssueField(field)) return ''
  return displayValue(address, field, address[field] ?? '')
}

// displayValue shows a province by its name, the way the form lists it, not its code.
export const displayValue = (
  address: Address,
  field: string,
  value: string
): string =>
  field === 'state_province_code' && value
    ? provinceName(address.country_code, value)
    : value

// An empty field in a suggestion keeps the shopper's own value. The province
// name follows its code so the summary and the label agree.
export const applyAddressSuggestion = (
  address: Address,
  suggestion: AddressSuggestion
): Address => {
  const next: Address = { ...address }
  if (suggestion.city) next.city = suggestion.city
  if (suggestion.postcode) next.postcode = suggestion.postcode
  if (suggestion.state_province_code) {
    next.state_province_code = suggestion.state_province_code
    next.state_province = provinceName(
      address.country_code,
      suggestion.state_province_code
    )
  }
  return next
}

// Applies the fixes the shopper accepted. An empty suggested value clears the
// field (a street merged into line 1 leaves line 2 empty). A province or
// country code drags its name along, the way the form would have set both.
export const applyAddressIssues = (
  address: Address,
  fixes: AddressIssue[]
): Address => {
  const next: Address = { ...address }
  for (const fix of fixes) {
    const field = fieldOf(fix)
    if (!isFixable(fix) || !isIssueField(field)) continue
    const value = fix.suggested_value ?? ''
    next[field] = value
    if (field === 'state_province_code') {
      next.state_province = provinceName(next.country_code, value)
    }
    if (field === 'country_code') {
      next.country = COUNTRY_NAMES[value] ?? value
    }
  }
  return next
}

const provinceName = (
  countryCode: string | undefined,
  provinceCode: string
): string =>
  getStaticProvinces(countryCode ?? '')?.find(
    (candidate) => candidate.code === provinceCode
  )?.name ?? provinceCode

// The only country a rule moves an address to today (a 980xx postcode under
// France is Monaco's); the names of the rest come from the form's country list.
const COUNTRY_NAMES: Record<string, string> = { MC: 'Monaco' }

// What the shopper reads for one candidate; empty fields fall back to their own values.
export const formatSuggestion = (
  address: Address,
  suggestion: AddressSuggestion
): string => {
  const postcode = suggestion.postcode ?? address.postcode ?? ''
  const city = suggestion.city ?? address.city ?? ''
  const province = suggestion.state_province_code
    ? ` (${suggestion.state_province_code})`
    : ''
  return `${postcode} ${city}${province}`.trim()
}
